import * as yup from 'yup';
import {
  addDays,
  addYears,
  isBefore,
  startOfToday,
  startOfYesterday,
  subYears,
} from 'date-fns';
import { parseDate } from '../../components/styled/forms/DateField';

const dateFormatErrorString = 'Date must be formatted as mm/dd/yyyy';

const ReferralSchema = yup
  .object({
    childFirstName: yup
      .string()
      .default('')
      .trim()
      .required('Child first name is required'),

    childLastName: yup
      .string()
      .default('')
      .trim()
      .required('Child last name is required'),

    childDateOfBirth: yup
      .date()
      .transform((value, originalValue) => {
        return parseDate(originalValue) || originalValue;
      })
      .required('Child date of birth is required')
      .typeError(dateFormatErrorString)
      .min(
        subYears(startOfToday(), 3),
        'Child date of birth cannot be more than 3 years ago'
      )
      .max(startOfYesterday(), 'Child date of birth must be in past')
      .test({
        name: 'ages out within 45 days',
        message:
          'Child will age out of Part C within 45 days; try Part B instead',
        test: value => {
          const childThirdBirthday = addYears(value, 3);
          const fortyFiveDaysFromNow = addDays(startOfToday(), 45);
          return isBefore(fortyFiveDaysFromNow, childThirdBirthday);
        },
      }),

    sexOfChild: yup
      .mixed()
      .default(undefined)
      .oneOf(['male', 'female', 'other'] as const)
      .notRequired(),

    otherSexOfChild: yup
      .string()
      .default('')
      .trim()
      .when('sexOfChild', {
        is: 'other',
        then: yup.string().required('Specified other sex of child is required'),
        otherwise: yup.string().notRequired(),
      }),

    primaryContactFirstName: yup.string().default('').trim().notRequired(),

    primaryContactLastName: yup.string().default('').trim().notRequired(),

    preferredContactMethod: yup
      .mixed()
      .default('')
      .oneOf(['', 'phone', 'email'] as const)
      .notRequired(),

    primaryContactPhone: yup
      .string()
      .default('')
      .trim()
      .when('preferredContactMethod', {
        is: 'phone',
        then: yup
          .string()
          .required('Preferred contact method details are required'),
        otherwise: yup.string().notRequired(),
      }),

    primaryContactEmail: yup
      .string()
      .default('')
      .trim()
      .email('Email address must be valid')
      .when('preferredContactMethod', {
        is: 'email',
        then: yup
          .string()
          .required('Preferred contact method details are required'),
        otherwise: yup.string().notRequired(),
      }),

    primaryContactPhysicalAddress: yup
      .string()
      .default('')
      .trim()
      .when('primaryContactSameAddresses', {
        is: false,
        then: yup.string().required('Physical address is required'),
        otherwise: yup.string().notRequired(),
      }),

    primaryContactSameAddresses: yup.boolean().default(true).notRequired(),

    primaryContactMailingAddress: yup
      .string()
      .default('')
      .trim()
      .when('primaryContactSameAddresses', {
        is: false,
        then: yup.string().required('Mailing address is required'),
        otherwise: yup.string().notRequired(),
      }),

    dateOfInitialReferral: yup
      .date()
      .transform((value, originalValue) => {
        return parseDate(originalValue) || originalValue;
      })
      .typeError(dateFormatErrorString)
      .when('childDateOfBirth', {
        is: value => value !== undefined,
        then: yup
          .date()
          .min(
            yup.ref('childDateOfBirth'),
            'Date of initial referral cannot be before child date of birth'
          ),
      })
      .max(startOfToday(), 'Date of initial referral cannot be in the future')
      .notRequired(),

    referringPartyFirstName: yup.string().default('').trim().notRequired(),

    referringPartyLastName: yup.string().default('').trim().notRequired(),

    referralReason: yup.string().default('').trim().notRequired(),
  })
  .defined();

export type FormFields = yup.InferType<typeof ReferralSchema>;

const initialValues: FormFields = {
  ...ReferralSchema.default(),
};

export { ReferralSchema, initialValues };
